import React, { useState, useEffect } from 'react';
import {
      CCard,
      CCardBody,
      CCardHeader,
      CCol,
      CRow,
      CButton,
      CBadge,
      CFormRange
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilLightbulb, cilSettings } from '@coreui/icons';
import { smartThingsService } from '../../../services/smartThingsService';
import SmartThingsConnect from './SmartThingsConnect';
import { toast } from 'react-toastify';

// Placeholder services for Google Cast and Voice Command
const googleCastService = {
      controlDevice: async (deviceId, action) => {
            console.log(`Google Cast controlling device ${deviceId} with action:`, action);
            // Placeholder implementation
            return Promise.resolve();
      }
};

const voiceCommandService = {
      processCommand: async (command) => {
            console.log('Processing voice command:', command);
            // Placeholder implementation
            return Promise.resolve();
      }
};

const sampleData = {
      devices: {
            lights: [
                  {
                        deviceId: 'sample-light-1',
                        label: 'Sample Living Room Light',
                        components: [{
                              capabilities: ['switch', 'switchLevel']
                        }],
                        status: {
                              switch: { value: 'on' },
                              switchLevel: { value: 75 }
                        }
                  },
                  {
                        deviceId: 'sample-light-2',
                        label: 'Sample Bedroom Light',
                        components: [{
                              capabilities: ['switch', 'switchLevel']
                        }],
                        status: {
                              switch: { value: 'off' },
                              switchLevel: { value: 50 }
                        }
                  }
            ],
            switches: [
                  {
                        deviceId: 'sample-switch-1',
                        label: 'Sample TV Switch',
                        components: [{
                              capabilities: ['switch']
                        }],
                        status: {
                              switch: { value: 'off' }
                        }
                  }
            ],
            googleCasts: [
                  {
                        deviceId: 'sample-cast-1',
                        label: 'Living Room TV',
                        components: [{
                              capabilities: ['cast']
                        }],
                        status: {
                              cast: { value: 'idle' }
                        }
                  },
                  {
                        deviceId: 'sample-cast-2',
                        label: 'Kitchen Speaker',
                        components: [{
                              capabilities: ['cast']
                        }],
                        status: {
                              cast: { value: 'playing' }
                        }
                  }
            ]
      }
};

const HomeAssistantDevices = () => {
      const [devices, setDevices] = useState(sampleData.devices);
      const [isConnected, setIsConnected] = useState(false);

      useEffect(() => {
            checkConnection();
      }, []);

      const checkConnection = async () => {
            try {
                  const token = await smartThingsService.getValidToken();
                  setIsConnected(!!token);
                  if (token) {
                        fetchDevices();
                  }
            } catch (error) {
                  console.error('Error checking connection:', error);
            }
      };

      const fetchDevices = async () => {
            try {
                  const token = await smartThingsService.getValidToken();
                  let categorizedDevices = {
                        lights: [...sampleData.devices.lights], // Start with sample data
                        switches: [...sampleData.devices.switches],
                        thermostats: [],
                        sensors: [],
                        googleCasts: [...sampleData.devices.googleCasts],
                  };

                  if (token) {
                        const response = await smartThingsService.getDevices(token);
                        // Add real devices to the categories
                        response.items.forEach(device => {
                              const capabilities = device.components[0].capabilities;
                              
                              if (capabilities.includes('switch') && capabilities.includes('switchLevel')) {
                                    categorizedDevices.lights.push(device);
                              } else if (capabilities.includes('switch')) {
                                    categorizedDevices.switches.push(device);
                              } else if (capabilities.includes('thermostat')) {
                                    categorizedDevices.thermostats.push(device);
                              } else if (capabilities.includes('contactSensor') || 
                                       capabilities.includes('motionSensor')) {
                                    categorizedDevices.sensors.push(device);
                              } else if (capabilities.includes('cast')) {
                                    categorizedDevices.googleCasts.push(device);
                              }
                        });
                  }

                  setDevices(categorizedDevices);
            } catch (error) {
                  console.error('Error fetching devices:', error);
                  toast.error('Failed to fetch devices');
                  setDevices(sampleData.devices); // Fallback to sample data on error
            }
      };

      const handleDeviceControl = async (deviceId, action) => {
            try {
                  const token = await smartThingsService.getValidToken();
                  if (!token) throw new Error('No valid token');

                  switch (action.type) {
                        case 'TOGGLE_SWITCH':
                              await smartThingsService.toggleSwitch(token, deviceId, action.state);
                              break;
                        case 'SET_LIGHT':
                              await smartThingsService.setLight(token, deviceId, action.params);
                              break;
                        case 'SET_THERMOSTAT':
                              await smartThingsService.setThermostat(token, deviceId, action.params);
                              break;
                  }

                  // Refresh devices after control
                  fetchDevices();
                  toast.success('Device updated successfully');
            } catch (error) {
                  console.error('Error controlling device:', error);
                  toast.error('Failed to control device');
            }
      };

      const handleGoogleCast = async (deviceId, action) => {
            try {
                  await googleCastService.controlDevice(deviceId, action);
                  toast.success('Google Cast device updated successfully');
            } catch (error) {
                  console.error('Error controlling Google Cast device:', error);
                  toast.error('Failed to control Google Cast device');
            }
      };

      const handleVoiceCommand = async (command) => {
            try {
                  await voiceCommandService.processCommand(command);
                  toast.success('Voice command executed successfully');
            } catch (error) {
                  console.error('Error executing voice command:', error);
                  toast.error('Failed to execute voice command');
            }
      };

      return (
            <CRow>
                  <CCol md={6}>
                        <CCard className="mb-4">
                              <CCardHeader>
                                    <CIcon icon={cilLightbulb} className="me-2" />
                                    Lights
                              </CCardHeader>
                              <CCardBody>
                                    {devices.lights.map(device => (
                                          <div key={device.deviceId} className="d-flex justify-content-between align-items-center mb-3">
                                                <span>{device.label}</span>
                                                <div>
                                                      <CButton
                                                            color={device.status?.switch?.value === 'on' ? 'success' : 'secondary'}
                                                            size="sm"
                                                            onClick={() => handleDeviceControl(device.deviceId, {
                                                                  type: 'TOGGLE_SWITCH',
                                                                  state: device.status?.switch?.value !== 'on'
                                                            })}
                                                            className="me-2"
                                                      >
                                                            {device.status?.switch?.value === 'on' ? 'ON' : 'OFF'}
                                                      </CButton>
                                                      {device.status?.switchLevel && (
                                                            <CFormRange
                                                                  min="0"
                                                                  max="100"
                                                                  value={device.status.switchLevel.value || 0}
                                                                  onChange={(e) => handleDeviceControl(device.deviceId, {
                                                                        type: 'SET_LIGHT',
                                                                        params: { brightness: parseInt(e.target.value) }
                                                                  })}
                                                            />
                                                      )}
                                                </div>
                                          </div>
                                    ))}
                              </CCardBody>
                        </CCard>
                  </CCol>
                  <CCol md={6}>
                        <CCard className="mb-4">
                              <CCardHeader>
                                    <CIcon icon={cilSettings} className="me-2" />
                                    Switches
                              </CCardHeader>
                              <CCardBody>
                                    {devices.switches.map(device => (
                                          <div key={device.deviceId} className="d-flex justify-content-between align-items-center mb-3">
                                                <span>{device.label}</span>
                                                <CButton
                                                      color={device.status?.switch?.value === 'on' ? 'success' : 'secondary'}
                                                      size="sm"
                                                      onClick={() => handleDeviceControl(device.deviceId, {
                                                            type: 'TOGGLE_SWITCH',
                                                            state: device.status?.switch?.value !== 'on'
                                                      })}
                                                >
                                                      {device.status?.switch?.value === 'on' ? 'ON' : 'OFF'}
                                                </CButton>
                                          </div>
                                    ))}
                              </CCardBody>
                        </CCard>
                  </CCol>
                  <CCol md={6}>
                        <CCard className="mb-4">
                              <CCardHeader>
                                    <CIcon icon={cilSettings} className="me-2" />
                                    Google Cast Devices
                              </CCardHeader>
                              <CCardBody>
                                    {devices.googleCasts.map(device => (
                                          <div key={device.deviceId} className="d-flex justify-content-between align-items-center mb-3">
                                                <span>{device.label}</span>
                                                <CButton
                                                      color="primary"
                                                      size="sm"
                                                      onClick={() => handleGoogleCast(device.deviceId, { type: 'PLAY' })}
                                                >
                                                      Play
                                                </CButton>
                                          </div>
                                    ))}
                              </CCardBody>
                        </CCard>
                  </CCol>
            </CRow>
      );
};

export default HomeAssistantDevices;
